import React, { useState } from 'react';
import { Zap, TrendingUp, BarChart3, AlertCircle, CheckCircle } from 'lucide-react';

const agents = [
  {
    icon: BarChart3,
    name: 'Inventory Agent',
    description: 'Forecasts demand for every SKU and optimizes stock across all store locations.',
    capabilities: ['Demand forecasting', 'Stock optimization', 'Waste prevention', 'Stockout alerts'],
  },
  {
    icon: TrendingUp,
    name: 'Ordering Agent',
    description: 'Drafts purchase orders, sends to suppliers on WhatsApp, and chases confirmations automatically.',
    capabilities: ['PO drafting', 'Supplier coordination', 'Delivery tracking', 'Cost optimization'],
  },
  {
    icon: AlertCircle,
    name: 'Exception Agent',
    description: 'Catches short-ships, substitutions, and pricing mismatches before they become problems.',
    capabilities: ['Short-ship detection', 'Exception escalation', 'Goods receipt matching', 'Quality checks'],
  },
  {
    icon: Zap,
    name: 'Performance Agent',
    description: 'Monitors shrinkage, waste, and margins across your retail locations in real-time.',
    capabilities: ['Shrinkage analysis', 'Waste tracking', 'Margin reporting', 'Performance optimization'],
  },
];

const integrations = [
  { icon: '💳', name: 'POS Systems', desc: 'Real-time sales data, transaction logs, and category performance', status: 'Connected', time: 'Last synced: 2 minutes ago' },
  { icon: '📦', name: 'Inventory Systems', desc: 'Stock levels, location counts, and bin tracking', status: 'Connected', time: 'Last synced: 5 minutes ago' },
  { icon: '🤝', name: 'Supplier APIs', desc: 'Vendor catalogs, pricing, delivery schedules, and performance', status: 'Connected', time: 'Last synced: 30 minutes ago' },
  { icon: '💬', name: 'WhatsApp Business', desc: 'PO sending and supplier communication automation', status: 'Connected', time: 'Last synced: 1 minute ago' },
  { icon: '📊', name: 'Accounting Software', desc: 'Goods receipts, invoicing, and financial reconciliation', status: 'Connected', time: 'Last synced: 15 minutes ago' },
  { icon: '📈', name: 'Analytics Dashboard', desc: 'Performance KPIs, waste tracking, and shrinkage reporting', status: 'Connected', time: 'Last synced: 3 minutes ago' },
];

export default function AgentShowcase() {
  const [approved, setApproved] = useState(false);

  // Hide any floating mascots in this section
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      #agents * {
        position: relative !important;
      }
      #agents svg[class*="mascot"],
      #agents img[alt*="mascot"],
      #agents [data-mascot] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  return (
    <section id="agents" className="max-w-7xl mx-auto px-6 py-24 relative z-10">
      {/* Header */}
      <div className="mb-16">
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 bg-[#2E6BFF]/10 rounded-full border border-[#2E6BFF]/20">
          <span className="inline-block w-1.5 h-1.5 bg-[#2E6BFF] rounded-full animate-pulse" />
          <span className="text-xs font-medium text-[#2E6BFF] tracking-wide uppercase">IN ACTION</span>
        </div>
        <h2 className="text-5xl sm:text-6xl font-bold tracking-tight mb-6">
          AI agents for retail & grocery
          <br />operations
        </h2>
      </div>

      {/* Sample Agent Recommendation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-24">
        {/* Recommendation Card */}
        <div className="lg:col-span-2">
          <p className="text-sm text-white/60 mb-4 uppercase tracking-wide">See it in action</p>

          {!approved ? (
            <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 rounded-2xl p-8 backdrop-blur">
              <div className="flex justify-between items-start mb-6 pb-6 border-b border-white/5">
                <div>
                  <p className="text-xs font-bold text-[#2E6BFF] tracking-wider uppercase mb-2">INVENTORY AGENT · RECOMMENDATION 1547</p>
                  <h3 className="text-2xl font-bold">Reorder Premium Basmati Rice (20kg)</h3>
                  <p className="text-sm text-white/50 mt-2">SKU GRC-7821 · Vendor Sunrise Foods Ltd</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-white/40 mb-1">Confidence</p>
                  <p className="text-3xl font-bold text-[#2E6BFF]">94%</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-4 mb-6 pb-6 border-b border-white/5">
                <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/40 mb-2">On hand</p>
                  <p className="text-xl font-bold">42 bags</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/40 mb-2">Stockout risk</p>
                  <p className="text-xl font-bold">8 days</p>
                </div>
                <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4">
                  <p className="text-xs text-white/40 mb-2">Recommended order</p>
                  <p className="text-xl font-bold">180 bags</p>
                </div>
              </div>

              {/* Why */}
              <div className="bg-[#2E6BFF]/5 border border-[#2E6BFF]/20 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-[#2E6BFF] mb-2">💡 Why this order</p>
                <p className="text-sm text-white/70">Weekend demand spike predicted. Current stock will deplete before Vendor's next delivery window on Monday. Premium rice is a high-margin item.</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setApproved(true)}
                  className="flex-1 px-4 py-3 bg-[#2E6BFF] hover:bg-[#1d4dd0] text-white font-semibold rounded-lg transition-all duration-200"
                >
                  ✓ Approve recommendation
                </button>
                <button className="flex-1 px-4 py-3 border border-white/10 hover:border-white/20 text-white font-semibold rounded-lg transition-all duration-200">
                  Correct the agent
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-gradient-to-br from-green-500/10 to-green-500/5 border border-green-500/20 rounded-2xl p-12 text-center backdrop-blur">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Recommendation Approved</h3>
              <p className="text-white/60 mb-6">#1547 has been processed and scheduled for ordering.</p>
              <button
                onClick={() => setApproved(false)}
                className="px-4 py-2 border border-green-500/20 text-green-400 font-semibold rounded-lg hover:bg-green-500/5 transition-all"
              >
                View another recommendation
              </button>
            </div>
          )}
        </div>

        {/* Agents Overview */}
        <div>
          <p className="text-sm text-white/60 mb-4 uppercase tracking-wide">Powered by 4 agents</p>
          <div className="space-y-3">
            {agents.map((agent, i) => {
              const Icon = agent.icon;
              return (
                <div key={i} className="bg-white/[0.03] border border-white/5 rounded-lg p-4 hover:bg-white/[0.05] transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-[#2E6BFF]/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="text-[#2E6BFF]" size={16} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white">{agent.name}</p>
                      <p className="text-xs text-white/50 line-clamp-1">{agent.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </section>
  );
}
